import { Table, TableColumnsType } from 'antd'
import React, { useState } from 'react'

import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'

import OrderTag from '../../../../../components/OrderTag'
import Pagination from '../../../../../components/Pagination'
import PageUrls from '../../../../../constants/page-urls'
import useAxios from '../../../../../hooks/useAxios'
import { EOrderStatus, IOrder, IOrderItem } from '../../../../../types/order'
import { IFilterMapped } from './CustomerReportFilter'

const PER_PAGE = 10

// สินค้าที่ไม่มีตัวเลือกเก็บ meta เป็น '0' หรือ '-' ไม่ใช่ค่าว่าง
const hasMeta = (value?: string) => !!value && value !== '0' && value !== '-'

interface ICustomerOrdersExpandProps {
  phone: string
  filter: IFilterMapped
}

const CustomerOrdersExpand = ({ phone, filter }: ICustomerOrdersExpandProps) => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(PER_PAGE)

  const { data, isLoading } = useAxios<{ records: IOrder[]; count: number }>({
    method: 'GET',
    url: '/order/v1',
    params: {
      search: phone,
      searchType: 'customer_phone',
      startDate: filter.startDate,
      endDate: filter.endDate,
      // หน้ารายงานกรองสินค้าทีละรายการ แต่ /order/v1 รับเป็น array
      productIds: filter.productId ? [filter.productId] : undefined,
      categoryIds: filter.categoryId ? [filter.categoryId] : undefined,
      page,
      perPage,
    },
  })

  const records = data?.records ?? []
  const total = data?.count ?? 0

  const columns: TableColumnsType<IOrder & { key: string }> = [
    {
      title: 'รหัสคำสั่งซื้อ',
      key: 'code',
      align: 'center' as const,
      width: 140,
      render: (_: unknown, order) =>
        order.objectId ? (
          <a
            href={`${PageUrls.Orders.Detail}/${order.objectId}`}
            target="_blank"
            rel="noreferrer"
          >
            {order.code ?? order.objectId}
          </a>
        ) : (
          order.code ?? '-'
        ),
    },
    {
      title: 'วันที่',
      key: 'orderDate',
      align: 'center' as const,
      width: 120,
      render: (_: unknown, order) =>
        order.orderDate ? dayjs(order.orderDate).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'สถานะ',
      key: 'status',
      align: 'center' as const,
      width: 140,
      render: (_: unknown, order) =>
        order.status ? (
          <OrderTag order={order} status={order.status as EOrderStatus} />
        ) : (
          '-'
        ),
    },
    {
      title: 'สินค้า',
      key: 'items',
      width: 360,
      render: (_: unknown, order) => {
        const items = order.items ?? []
        if (!items.length) {
          return '-'
        }
        return (
          <Box>
            {items.map((item: IOrderItem, index: number) => (
              <Typography
                key={item.objectId ?? `${item.productCode}-${index}`}
                variant="body2"
                color="text.primary"
              >
                {item.productName}
                {hasMeta(item.meta?.color) ? ` (${item.meta.color})` : ''}
                {hasMeta(item.meta?.size) ? ` (${item.meta.size})` : ''} ×
                {item.quantity}
                {item.free ? ' (แถม)' : ''}
              </Typography>
            ))}
          </Box>
        )
      },
    },
    {
      title: 'จำนวนชิ้น',
      key: 'quantity',
      align: 'center' as const,
      width: 100,
      render: (_: unknown, order) => order.quantity ?? 0,
    },
    {
      title: 'ยอดซื้อ',
      key: 'total',
      align: 'right' as const,
      width: 120,
      render: (_: unknown, order) =>
        `${(order.total ?? 0).toLocaleString()} บาท`,
    },
  ]

  return (
    <Box sx={{ padding: 1 }}>
      <Table
        loading={isLoading}
        size="small"
        columns={columns}
        dataSource={records.map((order: IOrder, index: number) => ({
          ...order,
          key: order.objectId ?? `${order.code ?? index}`,
        }))}
        locale={{ emptyText: 'ไม่พบคำสั่งซื้อในช่วงเวลาที่เลือก' }}
        bordered
        pagination={false}
      />
      {total > perPage && (
        <Pagination
          total={total}
          current={page}
          pageSize={perPage}
          onChange={(nextPage, nextPerPage) => {
            setPage(nextPage)
            setPerPage(nextPerPage)
          }}
        />
      )}
    </Box>
  )
}

export default CustomerOrdersExpand
